// Web-to-app attribution (see "Web-to-App Attribution Spec", Scott, 2026-06-22).
// The golfn.com website captures ad-click params (utm_*, gclid, rdt_cid) and shares
// them with this portal via URL params and the first-party `golfn_attr` cookie.
// Every in-portal link to the mobile app must go through an AppsFlyer OneLink
// stamped with these params and the user's id — never a raw store URL.

const ATTR_COOKIE = 'golfn_attr';
const ONELINK_BASE = 'https://golfn.onelink.me/VvtS';
const DOWNLOAD_PAGE_URL = 'https://www.golfn.com/download';
const GOLFN_APP_DEEP_LINK = 'golfn://home';

const ATTR_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'rdt_cid'] as const;

export type AttributionModel = Partial<Record<(typeof ATTR_PARAMS)[number], string>>;

export type OneLinkPath = 'qr' | 'badge' | 'email' | 'login';

function readAttributionCookie(): AttributionModel | null {
   const raw = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${ATTR_COOKIE}=`))
      ?.slice(ATTR_COOKIE.length + 1);
   if (!raw) return null;
   try {
      return JSON.parse(decodeURIComponent(raw));
   } catch {
      return null;
   }
}

function readAttributionFromUrl(): AttributionModel {
   const searchParams = new URLSearchParams(window.location.search);
   const attr: AttributionModel = {};
   ATTR_PARAMS.forEach((param) => {
      const value = searchParams.get(param);
      if (value) attr[param] = value;
   });
   return attr;
}

// First touch: writes URL attribution params to the shared cookie once, and only
// if the website (or an earlier visit) hasn't already written it.
export function persistAttribution(): void {
   if (readAttributionCookie()) return;

   const attr = readAttributionFromUrl();
   if (Object.keys(attr).length === 0) return;

   const value = encodeURIComponent(JSON.stringify(attr));
   const maxAge = 90 * 24 * 60 * 60;
   // domain/Secure only make sense on *.golfn.com — omitting them locally keeps the flow testable in dev
   const domainAttributes = window.location.hostname.endsWith('golfn.com') ? '; domain=.golfn.com; Secure' : '';
   document.cookie = `${ATTR_COOKIE}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${domainAttributes}`;
}

// The cookie is the first-touch source of truth; URL params only fill in when the
// cookie is missing (e.g. the browser blocked it but carried the params).
export function readAttribution(): AttributionModel {
   return readAttributionCookie() ?? readAttributionFromUrl();
}

// One link serves QR, store badges, and email — only af_sub5 differs. AppsFlyer
// routes it to the correct store per OS and carries the user id into the app.
export function buildOneLink(userId: string | undefined | null, path: OneLinkPath): string {
   const attr = readAttribution();
   const url = new URL(ONELINK_BASE);
   const set = (key: string, value?: string | null) => {
      if (value) url.searchParams.set(key, value);
   };
   set('pid', attr.utm_source);
   set('c', attr.utm_campaign);
   set('af_adset', attr.utm_term);
   set('af_ad', attr.utm_content);
   set('af_sub1', attr.gclid ?? attr.rdt_cid);
   set('deep_link_value', userId); // identity bridge — include whenever a user is signed in
   set('af_sub5', path);
   set('deep_link_sub1', path === 'login' ? 'login' : 'onboarding');
   return url.toString();
}

// Post-signup destination: the website's download modal handles the OneLink
// build, QR, badges, and the email-me-the-link path.
export function getDownloadPageUrl(userId: string, email?: string | null): string {
   const url = new URL(DOWNLOAD_PAGE_URL);
   url.searchParams.set('uid', userId);
   if (email) url.searchParams.set('email', email);
   return url.toString();
}

export async function hashEmail(email: string): Promise<string> {
   const data = new TextEncoder().encode(email.trim().toLowerCase());
   const digest = await window.crypto.subtle.digest('SHA-256', data);
   return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
}

// user_id must be the real GolfN id — it is the join key the app reports to
// Amplitude after login. Google Ads / Reddit conversion tags trigger off this event.
export async function pushAccountCreated(user: { id: string; email: string | null }): Promise<void> {
   const attr = readAttribution();
   (window as any).dataLayer = (window as any).dataLayer || [];
   (window as any).dataLayer.push({
      event: 'account_created',
      user_id: user.id,
      email_sha256: user.email ? await hashEmail(user.email) : undefined,
      utm_source: attr.utm_source,
      utm_campaign: attr.utm_campaign,
      utm_content: attr.utm_content,
   });
}

// Tries the app deep link first; if the app isn't installed, falls back to the
// attributed OneLink (which auto-routes to the correct store) — never a raw store URL.
export function openGolfnApp(userId?: string | null): void {
   window.location.href = GOLFN_APP_DEEP_LINK;
   setTimeout(() => {
      window.location.href = buildOneLink(userId, 'login');
   }, 1500);
}
